import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Box from '@nubank/nuds-web/components/Box/Box';
import Icon from '@nubank/nuds-web/components/Icon/Icon';

import { getActiveExperiments } from '../../../utils/googleOptimize/storage';

import { ExperimentsWidgetToggle } from './styles/ExperimentsWidgetToggle';
import { ExperimentsWidgetWrapper } from './styles/ExperimentsWidgetWrapper';
import { CloseButton } from './styles/CloseButton';
import { ClearButton } from './styles/ClearButton';
import { RadioInput } from './styles/RadioInput';

export default function ExperimentsWidget({
  activeExperiments,
  availableExperiments,
  saveExperimentsOnContext,
  resetExperimentsOnContext,
}) {
  const [visible, setVisible] = useState(false);
  const { formatMessage } = useIntl();

  useEffect(() => {
    const persistedExperiments = getActiveExperiments();
    if (persistedExperiments) {
      persistedExperiments.forEach(({ experimentId, variant }) => {
        saveExperimentsOnContext(variant, experimentId);
      });
    }
  }, []);

  return (
    <>
      {visible && (
        <ExperimentsWidgetWrapper>
          <Box
            tag="header"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            padding="4x"
          >
            <Typography variant="subtitle" color="white">Experiments</Typography>
            <CloseButton
              size="small"
              variant="basic"
              styleVariant="white"
              type="button"
              onClick={() => setVisible(false)}
              name="Close"
              iconProps={{
                name: 'x',
                title: formatMessage({ id: 'COMMON.MODAL.CLOSE_ACTION.TITLE' }),
              }}
            />
          </Box>

          <Box
            tag="section"
            paddingHorizontal="4x"
            paddingBottom="6x"
            paddingTop="2x"
            maxHeight="calc(90vh - 100px)"
            overflow="auto"
          >
            {availableExperiments.map(({ id, variants, name }, index) => {
              const variantsArray = Array.isArray(variants) ? variants : [variants];
              const experimentId = `experiment-${id}`;

              return (
                <Box marginTop={index ? '6x' : '0x'} key={experimentId}>
                  <Typography tag="span" variant="paragraph1" color="white">
                    {name}
                  </Typography>
                  &nbsp;
                  <Typography tag="span" color="primary.defaultL40" variant="text2">
                    {`(${id})`}
                  </Typography>
                  <Box tag="ul" paddingLeft="3x">
                    {['0', ...variantsArray].map(variant => {
                      const variantId = `experiment-${id}-variant-${variant}`;
                      const activeExperiment = activeExperiments[id]?.variant || '0';

                      return (
                        <Box tag="li" display="flex" alignItems="center" marginTop="3x" key={variantId}>
                          <RadioInput
                            type="radio"
                            id={variantId}
                            name={id}
                            value={variant}
                            checked={activeExperiment === variant}
                            onChange={() => saveExperimentsOnContext(variant, id)}
                          />
                          <Typography
                            variant="text2"
                            tag="label"
                            htmlFor={variantId}
                            marginLeft="2x"
                            color="white"
                          >
                            {variant === '0' ? 'Original' : `Variant ${variant}`}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <footer>
            <ClearButton type="button" onClick={resetExperimentsOnContext}>
              <Typography variant="text2">
                Clear all
              </Typography>
            </ClearButton>
          </footer>
        </ExperimentsWidgetWrapper>
      )}

      <ExperimentsWidgetToggle onClick={() => setVisible(true)}>
        <Icon name="arrow-up" title="Open widget" color="white" />
      </ExperimentsWidgetToggle>
    </>
  );
}

ExperimentsWidget.propTypes = {
  activeExperiments: PropTypes.objectOf(PropTypes.shape({ variant: PropTypes.string })),
  availableExperiments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    variants: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  })),
  resetExperimentsOnContext: PropTypes.func.isRequired,
  saveExperimentsOnContext: PropTypes.func.isRequired,
};

ExperimentsWidget.defaultProps = {
  activeExperiments: {},
  availableExperiments: [],
};
